import { makeStyles, tokens, Badge, shorthands } from "@fluentui/react-components";
import {
    BuildingRegular,
    PersonRegular,
    FilterRegular,
    CloudRegular,
    CalendarClockRegular,
    DocumentRegular
} from "@fluentui/react-icons";
import { createElement } from "react";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IRequestModelCellProps } from "./request-model-cell.props";

const layoutStyles = makeStyles({
    mainWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        ...shorthands.padding('4px', '0')
    },
    filterRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '6px'
    },
    filterIcon: {
        color: tokens.colorNeutralForeground3,
        flexShrink: 0
    },
    filterText: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground1,
        whiteSpace: 'nowrap',
        ...shorthands.overflow('hidden'),
        textOverflow: 'ellipsis'
    },
    filterValue: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1
    },
    scopeBadges: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px'
    },
    emptyState: {
        color: tokens.colorNeutralForeground4,
        fontSize: tokens.fontSizeBase200
    }
});

interface IFilterItem {
    icon: React.ElementType;
    label: string;
    value?: string;
    isBadge?: boolean;
    badges?: string[];
}

export default function RequestModelCell(props: IRequestModelCellProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const { requestModel } = props.reportRequest;

    // Helper: Get operator label from number
    const getOperatorLabel = (operator: number): string => {
        switch (operator) {
            case 1: return t('operatorContains');
            case 2: return t('operatorEndsWith');
            case 3: return t('operatorEquals');
            case 4: return t('operatorStartsWith');
            default: return '';
        }
    };

    // Helper: Get property label
    const getPropertyLabel = (property: string): string => {
        switch (property) {
            case 'ownerUpn': return t('ppaOwnerUpn');
            case 'title': return t('title');
            case 'url': return t('url');
            default: return property;
        }
    };

    // Helper: Extract site name from URL
    const getSiteName = (siteUrl: string): string => {
        if (!siteUrl) return '';
        const parts = siteUrl.split('/');
        return parts[parts.length - 1] || siteUrl;
    };

    // Helper: Get file size unit label
    const getFileSizeUnitLabel = (unit: number): string => {
        return unit === 1 ? 'GB' : 'MB';
    };

    // Helper: Get filter operator label (AND/OR)
    const getFilterOperatorLabel = (operator: number): string => {
        return operator === 1 ? t('ppaOr') : t('ppaAnd');
    };

    if (!requestModel) {
        return (
            <div className={styles.mainWrapper}>
                <span className={styles.emptyState}>—</span>
            </div>
        );
    }

    // Build structured filter items in the same order as the report form
    const filterItems: IFilterItem[] = [];

    // ===== SECTION 1: Report scope (workspace, owner, user, oneDrive) =====
    // These define what the report is scoped to

    // Workspace (specific workspace report)
    if (requestModel.workspace?.siteUrl) {
        filterItems.push({
            icon: BuildingRegular,
            label: getSiteName(requestModel.workspace.siteUrl)
        });
    }

    // Owner (owner-based reports)
    if (requestModel.owner?.upn) {
        filterItems.push({
            icon: PersonRegular,
            label: requestModel.owner.upn
        });
    }

    // User (user-specific reports)
    if (requestModel.user?.upn) {
        filterItems.push({
            icon: PersonRegular,
            label: requestModel.user.upn
        });
    }

    // OneDrive (OneDrive reports)
    if (requestModel.oneDrive) {
        if (requestModel.oneDrive.owner) {
            filterItems.push({
                icon: CloudRegular,
                label: requestModel.oneDrive.owner
            });
        } else if (requestModel.oneDrive.siteUrl) {
            filterItems.push({
                icon: CloudRegular,
                label: getSiteName(requestModel.oneDrive.siteUrl)
            });
        }
    }

    // ===== SECTION 2: Report-specific filters (appear first in forms) =====

    // File size report specific filters (version count, file size)
    if (requestModel.filterByVersionCount || requestModel.filterByFileSize) {
        const fileSizeFilters: string[] = [];

        if (requestModel.filterByVersionCount && requestModel.minVersionCount !== undefined) {
            fileSizeFilters.push(`${t('ppaMoreThan')} ${requestModel.minVersionCount} ${t('ppaVersions').toLowerCase()}`);
        }

        if (requestModel.filterByFileSize && requestModel.minFileSize !== undefined) {
            const unit = getFileSizeUnitLabel(requestModel.fileSizeUnit ?? 0);
            fileSizeFilters.push(`${t('ppaSizeLargerThan')} ${requestModel.minFileSize} ${unit}`);
        }

        if (fileSizeFilters.length > 0) {
            const combined = fileSizeFilters.length === 2 && requestModel.filterOperator !== undefined
                ? `${fileSizeFilters[0]} ${getFilterOperatorLabel(requestModel.filterOperator)} ${fileSizeFilters[1]}`
                : fileSizeFilters.join(', ');

            filterItems.push({
                icon: DocumentRegular,
                label: combined
            });
        }
    }

    // Inactivity days (usage reports)
    if (requestModel.inactivityDays !== undefined) {
        filterItems.push({
            icon: CalendarClockRegular,
            label: `${requestModel.inactivityDays} ${t('ppaInactivityDays').toLowerCase()}`
        });
    }

    // ===== SECTION 3: Workspace filter (appears after report-specific filters) =====

    // Workspace filter (tenant-wide with filter like URL contains, Title starts with)
    if (requestModel.workspaceFilter?.property && requestModel.workspaceFilter?.operator && requestModel.workspaceFilter?.value) {
        const property = getPropertyLabel(requestModel.workspaceFilter.property);
        const operator = getOperatorLabel(requestModel.workspaceFilter.operator).toLowerCase();
        const value = requestModel.workspaceFilter.value;
        filterItems.push({
            icon: FilterRegular,
            label: `${property} ${operator}`,
            value: `"${value}"`
        });
    }

    // ===== SECTION 4: Personal site filter (always last in forms) =====

    // Personal site filter (scope indicator) - show as badges
    if (requestModel.personalSiteFilter) {
        const scopes: string[] = [];
        if (requestModel.personalSiteFilter.includeWorkspaces) {
            scopes.push(t('ppaWorkspaces'));
        }
        if (requestModel.personalSiteFilter.includeOneDrive) {
            scopes.push(t('ppaOneDrives'));
        }
        if (scopes.length > 0) {
            filterItems.push({
                icon: BuildingRegular,
                label: '',
                isBadge: true,
                badges: scopes
            });
        }
    }

    // If no filters found, show empty state
    if (filterItems.length === 0) {
        return (
            <div className={styles.mainWrapper}>
                <span className={styles.emptyState}>—</span>
            </div>
        );
    }

    return (
        <div className={styles.mainWrapper}>
            {filterItems.map((item, index) => (
                <div key={index} className={styles.filterRow}>
                    <span className={styles.filterIcon}>
                        {createElement(item.icon, { fontSize: 16 })}
                    </span>
                    {item.isBadge && item.badges ? (
                        <div className={styles.scopeBadges}>
                            {item.badges.map((badge, badgeIndex) => (
                                <Badge
                                    key={badgeIndex}
                                    size="small"
                                    appearance="tint"
                                    color="informative">
                                    {badge}
                                </Badge>
                            ))}
                        </div>
                    ) : (
                        <span className={styles.filterText}>
                            {item.label}
                            {item.value && <span className={styles.filterValue}> {item.value}</span>}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}
